import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { AskScope } from './api';
import { useAskSummary } from './useAskSummary';

export type AiSummaryStatus = 'idle' | 'loading' | 'error';

/**
 * The request behind `AiSummary`, for a surface that places the button and the result
 * apart, like the card form's toolbar.
 */
export function useAiSummary({
  scope,
  buildPrompt,
  onSummaryGenerated,
}: {
  scope: AskScope;
  buildPrompt: () => string;
  onSummaryGenerated: (summary: string) => void;
}) {
  const { run } = useAskSummary();
  const [status, setStatus] = useState<AiSummaryStatus>('idle');

  const summarize = useCallback(async () => {
    setStatus('loading');
    const response = await run(buildPrompt(), scope);
    if (isNil(response)) {
      setStatus('error');
      return;
    }
    onSummaryGenerated(response);
    setStatus('idle');
  }, [run, scope, buildPrompt, onSummaryGenerated]);

  return { status, summarize };
}

/** The button is replaced by the widget while thinking and once a summary exists. */
export function isAiSummaryButtonShown(summary: string | undefined, status: AiSummaryStatus) {
  return isNil(summary) && status !== 'loading';
}
